import { z } from 'zod';
import type { PremiumInfo } from './premium';

export const PASSWORD_MIN = 8;

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN, `Password must be at least ${PASSWORD_MIN} characters`)
  .max(200, 'Password is too long')
  .refine((v) => /[a-zA-Z]/.test(v) && /[0-9\W_]/.test(v), {
    message: 'Use a mix of letters and numbers or symbols',
  });

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email('Enter a valid email address').max(254));

/** Common slips in the big mail services: the address looks fine but nobody would ever get it. */
const DOMAIN_TYPOS: Record<string, string> = {
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.con': 'gmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmai.com': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outlook.con': 'outlook.com',
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'iclod.com': 'icloud.com',
  'icloud.con': 'icloud.com',
};

/**
 * For an address being given to Tea+ (signing up, changing email). Signing in and password resets
 * keep emailSchema, so an account made before these checks can always get in.
 */
export const newEmailSchema = emailSchema.superRefine((email, ctx) => {
  const [local = '', domain = ''] = email.split('@');
  const fix = DOMAIN_TYPOS[domain];
  if (fix) ctx.addIssue({ code: 'custom', message: `Did you mean ${local}@${fix}?` });
  else if (
    local.length > 64 ||
    domain.split('.').some((label) => label.length > 63 || label.endsWith('-'))
  )
    ctx.addIssue({ code: 'custom', message: 'Enter a valid email address' });
});

export const DISPLAY_NAME_MAX = 50;

export const displayNameSchema = z
  .string()
  .trim()
  .min(1, 'Enter a display name')
  .max(DISPLAY_NAME_MAX, `Use ${DISPLAY_NAME_MAX} characters at most`)
  .regex(/^[A-Za-z0-9_.]+$/, 'Use only letters, numbers, underscores and full stops');

/** Display names are one per person worldwide, and "Test" and "test" count as the same name. */
export const displayNameKey = (name: string) => name.toLowerCase();

export const registerSchema = z.object({
  displayName: displayNameSchema,
  email: newEmailSchema,
  password: passwordSchema,
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Enter your password').max(200),
  remember: z.boolean().optional().default(true),
});
export type LoginInput = z.input<typeof loginSchema>;

export const changePasswordSchema = z.object({
  // Not needed to set the first password of an account made with Discord.
  currentPassword: z.string().max(200).optional(),
  newPassword: passwordSchema,
});

export const passwordResetRequestSchema = z.object({ email: emailSchema });
export const passwordResetConfirmSchema = z.object({
  token: z.string().min(20).max(200),
  newPassword: passwordSchema,
});

export const updateAccountSchema = z.object({
  displayName: displayNameSchema.optional(),
  email: newEmailSchema.optional(),
  currentPassword: z.string().max(200).optional(),
});

// The password is required whenever the account has one.
export const deleteAccountSchema = z.object({ password: z.string().max(200).optional() });

export type OnboardingStep =
  'profiles' | 'provider' | 'addons' | 'preferences' | 'complete' | 'done';

export interface SessionUser {
  id: string;
  email: string;
  displayName: string;
}

export interface SessionInfo {
  user: SessionUser;
  activeProfileId: string | null;
  onboardingStep: OnboardingStep;
  expiresAt: string;
  /** Tea+ Premium (in testing). Missing from servers before it. */
  premium?: PremiumInfo;
}

export interface SessionListItem {
  id: string;
  current: boolean;
  userAgent: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
}

export function passwordStrength(pw: string): { score: number; label: string } {
  let score = 0;
  if (pw.length >= PASSWORD_MIN) score++;
  if (pw.length >= 14) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score += 0.5;
  if (/[^a-zA-Z0-9]/.test(pw)) score += 0.5;
  const s = Math.min(4, Math.floor(score));
  return { score: s, label: ['Too weak', 'Weak', 'Fair', 'Strong', 'Excellent'][s]! };
}

/**
 * Starts signing in with Discord (login), linking it to the signed-in account for sign-in (link),
 * or connecting a profile's own Discord for its Rich Presence (profile).
 */
export const discordStartSchema = z.object({
  intent: z.enum(['login', 'link', 'profile']),
  /** profile: which profile (the active one when left out). */
  profileId: z.string().min(1).max(40).optional(),
  // redirect: this tab goes to Discord and comes back to /auth/discord (browsers).
  // poll: Discord opens in the system browser while the app waits (the Windows app).
  mode: z.enum(['redirect', 'poll']),
  remember: z.boolean().optional().default(true),
});
export type DiscordStartInput = z.input<typeof discordStartSchema>;

export const discordFinishSchema = z.object({
  state: z.string().min(20).max(100),
  token: z.string().min(20).max(100),
});

export interface DiscordStart {
  url: string;
  state: string;
  /** Only the window that started it holds this, so only it can finish. */
  token: string;
}

export interface DiscordLink {
  /** The Discord user id. */
  id: string;
  username: string;
  avatarUrl: string | null;
  linkedAt: string;
}

export interface DiscordStatus {
  /** Whether this server offers Discord at all. */
  available: boolean;
  /** The Discord application, for Rich Presence in the Windows app. */
  clientId: string | null;
  /** The account's Discord, used to sign in. */
  linked: DiscordLink | null;
  /** The profile's own Discord, where its Discord Presence shows. */
  profile: DiscordLink | null;
  hasPassword: boolean;
}

export type DiscordFinish =
  | { pending: true }
  | { pending: false; intent: 'login'; session: SessionInfo; created: boolean }
  | { pending: false; intent: 'link' | 'profile'; status: DiscordStatus };

export const presenceAvatarSchema = z.object({
  avatar: z
    .string()
    .max(300_000)
    .regex(/^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/, 'Invalid image'),
});
